import {
  HourglassEmptyOutlined,
  UpdateRounded,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import isNil from "lodash/isNil";

import CellRendererEditableLoanField from "../../_src_shared/agGrid/cellRenderer/CellRendererEditableLoanField";
import CellRendererLoanComments from "../../components/agGrid/cellRenderer/CellRendererLoanComments";
import CellRendererLoanName from "../../components/agGrid/cellRenderer/CellRendererLoanName";
import CellRendererStackedChip from "../../components/agGrid/cellRenderer/CellRendererStackedChip";
import CellRendererStatusStartDate, {
  statusStartDateFilterParams,
} from "../../components/agGrid/cellRenderer/CellRendererStatusStartDate";
import Flx from "../../components/layout/Flx";
import OpenInNewTabButton from "../../components/ui/buttons/OpenInNewTabButton";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { CellRendererRoleUser } from "../LoanOfficerPipelineScreen/LoanOfficerPipelineScreen";
import { getLoanBorrowerEntityName } from "../../utils/loanPipeline/getLoanBorrowerEntityName";
import {
  ACQUISITION_ON_HOLD_STATUS,
  acquisitionHeldStatus,
} from "../../pipelines/pipelineConfig";

// ════════════════════════════════════════════════════════════════════════════
// Acquisitions (postclose) pipeline columns
// ────────────────────────────────────────────────────────────────────────────
// The shared LoanOfficerPipelineTable defaults to the *underwriting* columns,
// which don't carry the trade tape at all (currentUPB, noteRate, bidPricePercent,
// tradeId, bulkTrade, …). This is the acquisition-specific column set, ordered to
// mirror the backend's `acquisitions_pipeline` projection
// (eastview-os app/routers/loan_acquisitions.py) and the guidance in
// frontend_documentation/loan-acquisitions.md §5:
//   • the borrower/entity column binds to the computed top-level `entityName`
//     (NOT the legacy `borrowerEntity` object),
//   • the trade economics + bulk-trade linkage are surfaced, and
//   • the postclose key dates (origination/funded/settlement/wire release) are
//     shown, with `expectedSettlementDate` as the editable soft target (and
//     the seller's `expectedClosingDate` beside it).
// Only `tradeId` + `bulkTrade` are flagged `display_column` on the model; the rest
// of the order/visibility here is our sensible default (users re-arrange + save
// layouts on top of it).
// ════════════════════════════════════════════════════════════════════════════

// noteRate / buyRate / interestRate are stored as decimals (0.11 → "11.00%").
const rateFormatter = (p) =>
  isNil(p?.value) ? "" : `${(Number(p.value) * 100).toFixed(2)}%`;

// bidPricePercent is already a percent-of-par number (101.25 → "101.250%"),
// NOT a decimal fraction — so it must NOT be multiplied by 100.
const parPercentFormatter = (p) =>
  isNil(p?.value) ? "" : `${Number(p.value).toFixed(3)}%`;

const ratioFormatter = (p) =>
  isNil(p?.value) ? "" : Number(p.value).toFixed(2);

const yesNoFormatter = (p) =>
  p?.value === true ? "Yes" : p?.value === false ? "No" : "";

// Loan Status with the acquisition sub-status stacked beneath it (the example
// loan is "Trade Review" / "Underwriting"). Kept local — the LO screen's version
// isn't exported and folds on a sibling Sub Status column we don't surface here.
const AcquisitionStatusCell = (params) => {
  if (params?.node?.group || !params?.data) return params?.value ?? null;
  const { loanStatus, subStatus } = params.data;
  if (!loanStatus) return params?.value ?? null;
  return (
    <Flx column sx={{ justifyContent: "center", minWidth: 0 }}>
      <Box
        sx={{
          lineHeight: 1.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {loanStatus}
      </Box>
      {subStatus ? (
        <Box
          sx={{
            fontSize: "11px",
            color: "#64748b",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {/* subStatus is the SF stage — on an Inactive (on hold) loan, the
              stage it was held at. */}
          {loanStatus === ACQUISITION_ON_HOLD_STATUS
            ? `held at ${subStatus}`
            : subStatus}
        </Box>
      ) : null}
    </Flx>
  );
};

// The current milestone's tracked steps from the computed `stageTracking`
// ({ milestone, step, field, value, done } — ACQUISITION_STAGE_STEPS in
// loan_acquisitions.py). An on-hold loan reads the steps of its held-at stage.
const currentStageSteps = (data) => {
  const milestone = acquisitionHeldStatus(data) || data?.loanStatus;
  return (Array.isArray(data?.stageTracking) ? data.stageTracking : []).filter(
    (s) => s?.milestone === milestone,
  );
};

const stageStepsValueGetter = (p) => {
  const steps = currentStageSteps(p?.data);
  if (!steps.length) return null;
  const done = steps.filter((s) => s.done).length;
  const next = steps.find((s) => !s.done);
  return next
    ? `${done}/${steps.length} · Next: ${next.step}`
    : `${done}/${steps.length} · Complete`;
};

const stageStepsTooltip = (p) => {
  const steps = currentStageSteps(p?.data);
  if (!steps.length) return null;
  return steps.map((s) => `${s.done ? "✓" : "○"} ${s.step}`).join("\n");
};

/**
 * Build the Acquisitions pipeline column defs.
 * @param {string} drilldownBase route base for the Loan # new-tab link
 *   (defaults to /acquisition so the postclose drilldown opens, not /loan).
 */
export const buildAcquisitionsColumnDefs = (drilldownBase = "/acquisition") => [
  { hide: true, headerName: "_id", field: "_id" },
  {
    headerName: "Loan #",
    field: "loanNumber",
    pinned: "left",
    minWidth: 90,
    maxWidth: 90,
    width: 90,
    suppressAutoSize: true,
    suppressHeaderFilterButton: true,
    suppressFloatingFilterButton: true,
    flex: 0,
    getQuickFilterText: (params) => (params.value ? String(params.value) : ""),
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
      if (params.node.group) return null;
      return (
        <OpenInNewTabButton
          linkIcon
          to={`${drilldownBase}/${params?.data?._id}`}
        >
          {params?.data?.loanNumber}
        </OpenInNewTabButton>
      );
    },
  },
  {
    headerName: "Created",
    field: "createDateTime",
    type: "date",
    // UTC timestamp — format UTC → Eastern (matches the LO pipeline).
    valueFormatter: (p) => formatDateTime(p?.value, "date"),
    minWidth: 115,
    maxWidth: 115,
    width: 115,
    flex: 0,
    wrapHeaderText: true,
    suppressHeaderFilterButton: true,
    suppressFloatingFilterButton: true,
    cellRenderer: (p) =>
      p.node.group ? null : (
        <CellRendererStackedChip
          icon={<HourglassEmptyOutlined className="thin-8" />}
          params={p}
          compact={p?.context?.isCompact}
        />
      ),
  },
  {
    headerName: "Loan Name",
    field: "loanName",
    minWidth: 280,
    width: 360,
    suppressSizeToFit: true,
    suppressAutoSize: true,
    suppressHeaderFilterButton: true,
    suppressFloatingFilterButton: true,
    autoHeight: true,
    headerTooltip: "Go to loan",
    cellStyle: { cursor: "pointer" },
    valueGetter: (params) =>
      isNil(params?.data?.loanName)
        ? `Loan #${params?.data?.loanNumber}`
        : params?.data?.loanName,
    cellRenderer: (params) =>
      params.node.group ? null : (
        <CellRendererLoanName {...params} compact={params?.context?.isCompact} />
      ),
  },
  {
    headerName: "Comments",
    field: "comments",
    width: 320,
    minWidth: 220,
    flex: 0,
    autoHeight: true,
    wrapText: true,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    suppressHeaderFilterButton: true,
    suppressFloatingFilterButton: true,
    cellStyle: { cursor: "pointer" },
    cellRenderer: (params) =>
      params.node.group ? null : (
        <CellRendererLoanComments
          {...params}
          compact={params?.context?.isCompact}
        />
      ),
  },
  {
    // When the loan entered its current loanStatus (the LOS "last stage
    // change"). A UTC timestamp from the backend; the cell shows the days
    // since (Eastern calendar days), the dateTime type keeps sort/filter/export
    // on the date. Null on loans whose status moved without a history entry.
    headerName: "Current Status Start Date",
    field: "currentStatusStartDate",
    type: "dateTime",
    cellRenderer: CellRendererStatusStartDate,
    filterParams: statusStartDateFilterParams,
    width: 210,
    minWidth: 140,
    flex: 0,
    wrapHeaderText: true,
  },
  {
    // Computed by the acquisitions pipeline endpoint from
    // currentStatusStartDate as fractional days (2dp). Shown as whole days;
    // the raw number stays the value so sort / filter / export are exact.
    headerName: "Days In Current Status",
    field: "daysInCurrentStatus",
    type: "number",
    valueFormatter: (p) =>
      isNil(p?.value) || Number.isNaN(Number(p.value))
        ? ""
        : Math.floor(Number(p.value)).toLocaleString(),
    width: 130,
    minWidth: 100,
    flex: 0,
    wrapHeaderText: true,
  },
  {
    headerName: "Loan Status",
    field: "loanStatus",
    width: 150,
    minWidth: 120,
    flex: 0,
    cellStyle: { cursor: "pointer", display: "flex", alignItems: "center" },
    cellRenderer: AcquisitionStatusCell,
  },
  {
    // Sub-stage progress inside the current milestone (owner 2026-10-02):
    // "2/4 · Next: Submitted for TPR". Blank for milestones with no steps.
    headerName: "Stage Steps",
    colId: "stageSteps",
    valueGetter: stageStepsValueGetter,
    tooltipValueGetter: stageStepsTooltip,
    width: 220,
    minWidth: 140,
    flex: 0,
  },
  {
    headerName: "Seller (Correspondent)",
    field: "sellerCorrespondentName",
    width: 160,
    minWidth: 120,
    flex: 0,
  },
  {
    headerName: "Borrower Entity",
    // Resolved across every projection shape (computed `entityName` /
    // `borrowerEntityName`, legacy `borrowerEntity`, entities-first
    // `entities[]`) — binding to one key alone left the column all "N/A".
    field: "entityName",
    minWidth: 160,
    valueGetter: (p) => getLoanBorrowerEntityName(p?.data),
  },
  {
    headerName: "Loan Product Type",
    field: "loanProductType",
    width: 155,
    minWidth: 120,
    flex: 0,
  },
  { headerName: "Loan Purpose", field: "loanPurpose", width: 130, flex: 0 },
  {
    headerName: "Base Loan Amount",
    field: "baseLoanAmount",
    type: "dollar",
  },
  { headerName: "Current UPB", field: "currentUPB", type: "dollar" },
  // Shown by default beside Current UPB — the model sets no default-hidden
  // flag on originalUPB, so it is visible unless a user hides it.
  { headerName: "Original UPB", field: "originalUPB", type: "dollar" },
  {
    headerName: "Note Rate",
    field: "noteRate",
    valueFormatter: rateFormatter,
    width: 110,
    minWidth: 90,
    flex: 0,
  },
  {
    headerName: "Bid Price %",
    field: "bidPricePercent",
    valueFormatter: parPercentFormatter,
    width: 120,
    minWidth: 100,
    flex: 0,
  },
  {
    headerName: "Purchase Price",
    field: "purchasePriceAmount",
    type: "dollar",
  },
  {
    headerName: "Premium / Discount",
    field: "premiumDiscount",
    type: "dollar",
    hide: true,
  },
  {
    headerName: "Trade ID",
    field: "tradeId",
    width: 130,
    minWidth: 100,
    flex: 0,
  },
  {
    headerName: "Trade Status",
    field: "tradeStatus",
    width: 130,
    minWidth: 100,
    flex: 0,
  },
  {
    headerName: "Bulk Trade",
    field: "bulkTrade",
    valueFormatter: yesNoFormatter,
    width: 110,
    minWidth: 90,
    flex: 0,
  },
  {
    headerName: "Trade Loan Count",
    field: "tradeLoanCount",
    type: "number",
    hide: true,
  },
  {
    headerName: "Servicing Type",
    field: "servicingType",
    hide: true,
  },
  {
    headerName: "Due Diligence Status",
    field: "dueDiligenceStatus",
    width: 150,
    minWidth: 120,
    flex: 0,
  },
  {
    headerName: "Note Review",
    field: "noteReviewStatus",
    width: 130,
    minWidth: 110,
    flex: 0,
  },
  { headerName: "Asset Status", field: "assetStatus", hide: true },
  { headerName: "DSCR", field: "dscr", valueFormatter: ratioFormatter, hide: true },
  {
    headerName: "Open Conditions",
    field: "openConditionsCount",
    type: "number",
    width: 120,
    minWidth: 100,
    flex: 0,
  },
  {
    headerName: "Open Exceptions",
    field: "openExceptionsCount",
    type: "number",
    hide: true,
  },
  {
    headerName: "Processor",
    field: "processor",
    cellRenderer: CellRendererRoleUser,
    width: 120,
    minWidth: 80,
    flex: 0,
  },
  {
    headerName: "Loan Closer",
    field: "loanCloser",
    cellRenderer: CellRendererRoleUser,
    width: 120,
    minWidth: 80,
    flex: 0,
  },
  {
    headerName: "Underwriter",
    field: "underwriter",
    cellRenderer: CellRendererRoleUser,
    width: 120,
    minWidth: 80,
    flex: 0,
  },
  // Pre-UW / QC assignees — hidden by default (opt in from the column chooser).
  {
    headerName: "Junior Underwriter",
    field: "preUnderwriter",
    cellRenderer: CellRendererRoleUser,
    width: 140,
    minWidth: 80,
    flex: 0,
    hide: true,
  },
  {
    headerName: "QC Review",
    field: "qc_analyst",
    cellRenderer: CellRendererRoleUser,
    width: 120,
    minWidth: 80,
    flex: 0,
    hide: true,
  },
  {
    headerName: "Account Manager",
    field: "accountManager",
    cellRenderer: CellRendererRoleUser,
    width: 120,
    minWidth: 80,
    flex: 0,
    hide: true,
  },
  {
    headerName: "Structurer",
    field: "structurer",
    cellRenderer: CellRendererRoleUser,
    width: 120,
    minWidth: 80,
    flex: 0,
    hide: true,
  },
  {
    headerName: "Salesperson",
    field: "salesperson",
    cellRenderer: CellRendererRoleUser,
    width: 120,
    minWidth: 80,
    flex: 0,
    hide: true,
  },
  // Key dates (loan-acquisitions.md "Key dates", owner 2026-10-02): has the
  // loan closed at the correspondent? Yes → Note Date + the correspondent's
  // Funded Date; No → Expected Closing Date. fundedDate is never our wire.
  {
    headerName: "Loan Closed?",
    field: "loanClosed",
    valueFormatter: yesNoFormatter,
    width: 120,
    minWidth: 90,
    flex: 0,
  },
  {
    headerName: "Note Date",
    field: "originationDate",
    type: "date",
    hide: true,
  },
  { headerName: "Funded Date", field: "fundedDate", type: "date", hide: true },
  {
    headerName: "Trade Date",
    field: "tradeDate",
    type: "date",
    hide: true,
  },
  // Server-derived: the firm settlementDate when set, else the expected one —
  // the date the desk plans and sorts on.
  {
    headerName: "Settlement Target",
    field: "settlementTargetDate",
    type: "date",
    width: 160,
    minWidth: 130,
    flex: 0,
    headerTooltip:
      "Settlement Date when firm, otherwise Expected Settlement Date",
  },
  // The acquisitions "Expected Closing Date": the soft target for when we
  // settle (fund) the purchase. Click-to-edit like the preclose column; the
  // grid sits inside the acquisitions PipelineProvider, so the save goes to
  // /loan_acquisitions/update_loan_data. Settlement Date further right is the
  // FIRM date (frontend_documentation/acquisitions-expected-settlement-date.md).
  {
    headerName: "Expected Settlement Date",
    field: "expectedSettlementDate",
    type: "date",
    width: 180,
    minWidth: 150,
    flex: 0,
    cellRenderer: CellRendererEditableLoanField,
    cellStyle: { cursor: "pointer", display: "flex", alignItems: "center" },
  },
  // The SELLER's expected close, for loans locked with us before the
  // correspondent closes them — a different date from the settlement target.
  {
    headerName: "Expected Closing Date",
    field: "expectedClosingDate",
    type: "date",
    width: 170,
    minWidth: 140,
    flex: 0,
    cellRenderer: CellRendererEditableLoanField,
    cellStyle: { cursor: "pointer", display: "flex", alignItems: "center" },
  },
  {
    headerName: "Settlement Date (Purchase Funded)",
    field: "settlementDate",
    type: "date",
    width: 230,
    minWidth: 120,
    flex: 0,
  },
  {
    headerName: "Wire Release Date",
    field: "wireReleaseDate",
    type: "date",
    hide: true,
  },
  {
    headerName: "Maturity Date",
    field: "maturityDate",
    type: "date",
    hide: true,
  },
  {
    headerName: "Originator",
    field: "originator",
    width: 115,
    minWidth: 100,
    flex: 0,
    hide: true,
  },
  {
    headerName: "Updated",
    field: "updateDateTime",
    type: "date",
    valueFormatter: (p) => formatDateTime(p?.value, "date"),
    minWidth: 115,
    width: 115,
    flex: 0,
    hide: true,
    wrapHeaderText: true,
    suppressHeaderFilterButton: true,
    cellRenderer: (p) =>
      p.node.group ? null : (
        <CellRendererStackedChip
          icon={<UpdateRounded className="thin-8" />}
          params={p}
          compact={p?.context?.isCompact}
        />
      ),
  },
  {
    headerName: "Last Viewed",
    field: "lastViewDateTime",
    type: "date",
    hide: true,
  },
  { headerName: "Pipeline", field: "pipeline", hide: true },
  { headerName: "Permitted Users", field: "permittedUsers", hide: true },
];

export default buildAcquisitionsColumnDefs;
